import { useState, useEffect, useRef, Fragment } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { trackLandingViewed, getVariant } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Shield, Zap, Key, Mail, Inbox, Clock,
  Check, Loader2, Lock, RefreshCcw, ChevronDown,
  Eye, EyeOff, Fingerprint, Layers, RotateCcw, LogIn,
} from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import TurnstileWidget from "@/components/TurnstileWidget";
import DocumentHead from "@/components/DocumentHead";

// SIMPLIFIED FLOW (Apr 30 2026) — reduce to one CTA, hide secondary paths.
// Set to false to restore the full custom-address card + advanced flows.
const SIMPLIFIED_FLOW = true;

export default function LandingPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    console.log("[LandingPage] Mounted", {
      url: window.location.href, user: !!user, loading,
      localStorage_anon_inbox: localStorage.getItem("anon_inbox_session"),
      localStorage_anon_claims: localStorage.getItem("anon_claim_codes"),
    });
  }, [user, loading]);

  const landingTrackedRef = useRef(false);

  useEffect(() => {
    if (!landingTrackedRef.current) { landingTrackedRef.current = true; trackLandingViewed(); }
  }, []);

  // PROMPT 027 + responsive fix — Secondary trial entry. Free-alias
  // self-signup is back as a SECONDARY conversion path (not the primary
  // entry — that's pricing). The 30-min trial inbox is the conversion
  // stage; the upgrade prompts live INSIDE the inbox, not here. Backend
  // already hard-caps at 30 minutes via claim_code_expires_at.
  const TRIAL_DOMAIN = "@mail.quickinbox.app";
  const [trialAlias, setTrialAlias] = useState("");
  const [trialError, setTrialError] = useState("");
  const [trialCreating, setTrialCreating] = useState(false);
  const ALIAS_STRIP_RE = /[^a-zA-Z0-9._-]/g;
  // Strip the well-known domain suffix if the user typed a full email,
  // then drop disallowed characters and lowercase. This lets users paste
  // either "marc" or "marc@mail.quickinbox.app" — both normalize to "marc".
  const normalizeLocalPart = (raw) => {
    let s = String(raw || "").trim().toLowerCase();
    if (s.endsWith(TRIAL_DOMAIN)) s = s.slice(0, -TRIAL_DOMAIN.length);
    // Also handle a stray "@" or partial domain typed by the user.
    const atIdx = s.indexOf("@");
    if (atIdx >= 0) s = s.slice(0, atIdx);
    return s.replace(ALIAS_STRIP_RE, "");
  };
  const validateTrialAlias = (val) => {
    if (!val) return ""; // empty is OK — Option B (random)
    if (/\.\./.test(val)) return "Dots can't be next to each other";
    if (/^[.\-_]|[.\-_]$/.test(val)) return "Can't start or end with a special char";
    if (val.length < 3) return "At least 3 characters";
    return "";
  };
  const handleTrialChange = (val) => {
    const clean = normalizeLocalPart(val);
    setTrialAlias(clean);
    if (trialError) setTrialError("");
  };

  // PROMPT 030 — Live availability check (debounced). Calls the existing
  // POST /api/free/check-address. Renders next to the live preview as a
  // neutral, non-judgmental status: "Address available" / "Already taken"
  // / "Letters, numbers, dots only" — never as an error toast.
  const [trialAvail, setTrialAvail] = useState(null); // null | {status: "ok"|"taken"|"invalid"|"checking", text: string}
  useEffect(() => {
    if (!trialAlias) { setTrialAvail(null); return; }
    // Run frontend validation first — if it fails, surface that immediately
    // and skip the network call.
    const localErr = validateTrialAlias(trialAlias);
    if (localErr) {
      setTrialAvail({ status: "invalid", text: localErr });
      return;
    }
    setTrialAvail({ status: "checking", text: "Checking…" });
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/free/check-alias", { params: { local_part: trialAlias } });
        if (cancelled) return;
        if (data?.available) {
          setTrialAvail({ status: "ok", text: "Address available" });
        } else if (data?.valid === true) {
          setTrialAvail({ status: "taken", text: "Already taken — try another" });
        } else {
          setTrialAvail({ status: "invalid", text: "Letters, numbers, dots only" });
        }
      } catch {
        if (!cancelled) setTrialAvail(null); // soft-fail: hide the badge
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [trialAlias]);
  const handleStartTrial = async (mode) => {
    // mode: "custom" → must have a typed alias; "random" → ignore input
    const wantCustom = mode === "custom";
    if (wantCustom) {
      const err = validateTrialAlias(trialAlias);
      if (err) { setTrialError(err); return; }
    }
    setTrialCreating(true);
    try {
      const payload = {};
      if (wantCustom && trialAlias.trim()) {
        payload.local_part = trialAlias.trim();
      }
      const { data } = await api.post("/free/create-alias", payload);
      // Hint for the inbox conversion banner: did the user pick this
      // address themselves or did the backend roll a random one?
      try {
        if (wantCustom) {
          localStorage.setItem("qib_trial_alias_choice", `custom:${(data.local_part || trialAlias.trim()).toLowerCase()}`);
        } else {
          localStorage.removeItem("qib_trial_alias_choice");
        }
      } catch { /* private mode — ignore */ }
      navigate(`/free/${data.claim_code}`, {
        state: {
          justCreated: true,
          alias: data,
          skipCreatedScreen: true,
          inbox_token: data.inbox_token,
          access_code: data.access_code,
          aliasChoiceMode: wantCustom ? "custom" : "random",
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409) {
        setTrialError("This alias is already taken — try another");
      } else {
        toast.error(typeof detail === "string" && detail ? detail : "Couldn't start trial. Try again.");
      }
    } finally {
      setTrialCreating(false);
    }
  };

  // ── Login state ──
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  // Ref used to scroll + focus the SIGN IN input when the Landing is opened
  // via /?focus=signin (the new canonical replacement for the legacy /login).
  const loginEmailRef = useRef(null);
  // Turnstile token — required ONLY for falling back to /api/auth/login.
  // PRO login (/api/pro/login) does not require it, so we keep the widget
  // non-blocking and let users still complete a PRO login without solving.
  const [turnstileToken, setTurnstileToken] = useState("");

  // ── Legacy-route handoff: /?focus=signin brings users sent from /login here ──
  useEffect(() => {
    if (searchParams.get("focus") !== "signin") return;
    // Defer so the hero has mounted and fixed-header offsets are correct.
    const t = setTimeout(() => {
      const box = document.querySelector("[data-testid='landing-login-box']");
      if (box) box.scrollIntoView({ behavior: "smooth", block: "center" });
      loginEmailRef.current?.focus?.({ preventScroll: true });
    }, 250);
    return () => clearTimeout(t);
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const identifier = loginEmail.trim().toLowerCase();
    const password = loginPassword.trim();
    if (!identifier || !password) return;
    setLoggingIn(true);

    // ── Identifier routing heuristics ──
    // The Landing SIGN IN is the ONE login entry point. We decide which backend
    // to hit from the identifier shape alone — without merging auth systems:
    //   * ends with @mail.quickinbox.app → PRO (try first; canonical alias domain)
    //   * ends with @quickinbox.app      → PRO (legacy alias domain — still accepted on backend)
    //   * no @ at all (plain username)   → PRO (try first)
    //   * otherwise (external email)     → main /api/auth/login only
    // If PRO returns an auth error, we transparently fall back to main auth.
    // We never call both endpoints for a single attempt past that fallback so
    // the rate-limit counter on the PRO side doesn't bloat for main-auth users.
    const PRO_DOMAINS = ["@mail.quickinbox.app", "@quickinbox.app"];
    const looksLikePro =
      PRO_DOMAINS.some((d) => identifier.endsWith(d)) || !identifier.includes("@");

    try {
      if (looksLikePro) {
        try {
          const { data } = await api.post("/pro/login", {
            username: identifier,
            password,
          });
          if (data?.requires_2fa && data?.temp_token) {
            // 2FA prompt is only built on /pro/login. Hand off the temp_token
            // via sessionStorage so the user finishes verification there.
            sessionStorage.setItem("pro_login_temp_token", data.temp_token);
            navigate("/pro/login?step=2fa", { replace: true });
            return;
          }
          toast.success("Logged in");
          navigate("/pro/inbox", { replace: true });
          return;
        } catch (proErr) {
          const status = proErr?.response?.status;
          // 429 / rate-limited / abuse → surface the PRO error directly.
          if (status === 429 || status === 403) {
            toast.error(getErrorMessage(proErr, "Too many attempts. Please try again later."));
            return;
          }
          // 401 (invalid creds) or 400/404 (unknown account) → fall through to
          // main-site auth. This lets a user with no PRO account but a regular
          // email-style username (no `@`) still reach /inbox.
        }
      }

      // Main-site /api/auth/login (regular + admin users)
      // Requires a Turnstile token — the widget below the form solves
      // automatically when the site key allows (test key always passes).
      if (!turnstileToken) {
        toast.error("Please verify you are human before signing in.");
        return;
      }
      const result = await login(identifier, password, { turnstile_token: turnstileToken });
      // Turnstile tokens are one-time. Reset so a retry solves a fresh challenge.
      setTurnstileToken("");
      if (result?.requires_2fa || result?.requires_2fa_setup) {
        navigate("/admin/2fa-setup", { state: { temp_token: result.temp_token, requires_setup: result.requires_2fa_setup } });
      } else if (result?.role === "admin" || result?.role === "superadmin") {
        navigate("/housekeeping");
      } else {
        navigate("/inbox");
      }
    } catch (err) {
      // If main-auth failed (e.g., 403 shield / 401 creds), clear the used token
      // so the user can re-verify on the next attempt.
      setTurnstileToken("");
      toast.error(getErrorMessage(err, "Invalid email/username or password."));
    } finally {
      setLoggingIn(false);
    }
  };

  // ── Pricing (fetched from backend — stripe_price_id is the source of truth) ──
  const [plans, setPlans] = useState([]);
  const [promo, setPromo] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [checkoutLoadingKey, setCheckoutLoadingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing/active");
        setPlans(data?.plans || []);
        setPromo(data?.promo || null);
      } catch { /* non-fatal, pricing section just stays empty */ }
      finally { setPlansLoading(false); }
    })();
  }, []);

  const handleUpgrade = async (plan) => {
    if (!plan?.stripe_price_id) { toast.error("Plan unavailable"); return; }
    setCheckoutLoadingKey(plan.key);
    try {
      const origin_url = window.location.origin;
      // Mirror PricingPage's branch: authed users have a tenant/alias → plan_id flow;
      // anonymous visitors use the guest pro-checkout that takes stripe_price_id directly.
      const endpoint = user ? "/payments/checkout" : "/payments/pro-checkout";
      const body = user
        ? { plan_id: plan.key, origin_url }
        : { stripe_price_id: plan.stripe_price_id, origin_url };
      const { data } = await api.post(endpoint, body);
      if (data?.url) window.location.href = data.url;
      else toast.error("Checkout unavailable");
    } catch (err) {
      toast.error(getErrorMessage(err, "Checkout failed"));
    } finally {
      setCheckoutLoadingKey(null);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const dashboardPath = isAdmin ? "/housekeeping" : "/inbox";

  return (
    <div className="min-h-screen bg-l-bg text-l-text selection:bg-l-accent/30" data-testid="landing-page">
      <DocumentHead
        title="QuickInbox — Anonymous Email Made Simple"
        description="Create anonymous email aliases in seconds. Keep your real inbox private, block spam, and stay in control of every sign-up."
        path="/"
      />

      {/* ═══ NAV ═══ */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-l-border-soft bg-l-bg/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-quickinbox.png"
              alt="QuickInbox"
              width="80"
              height="80"
              className="h-20 w-auto select-none"
              draggable="false"
              data-testid="landing-logo"
            />
            <span className="font-heading text-2xl font-semibold tracking-tight text-l-text">QuickInbox</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => scrollTo("pricing")}
              className="text-l-text-muted hover:text-l-text text-l-footer h-9"
              data-testid="nav-pricing"
            >
              Pricing
            </Button>
            {!loading && user ? (
              <Link to={dashboardPath} className="l-chip-nav" data-testid="nav-dashboard">
                <span className="l-chip-nav__icon"><Shield size={12} /></span>
                Dashboard <ArrowRight size={13} strokeWidth={2.5} />
              </Link>
            ) : (
              <Link to="/login">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-l-text-muted hover:text-l-text text-l-footer h-9"
                  data-testid="nav-signin"
                >
                  Sign in
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
      {/* ═══ HERO — 35/65 split: marketing copy (left) + stacked auth cards (right) ═══ */}
      <section id="alias-input" className="relative pt-32 pb-16 sm:pt-36 sm:pb-20 lg:pt-40 lg:pb-24 overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute top-[-240px] left-1/3 -translate-x-1/2 w-[700px] h-[700px] bg-l-accent/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5">
          <div
            className="grid grid-cols-1 lg:grid-cols-[35fr_65fr] gap-10 lg:gap-14 items-center"
            data-testid="landing-hero-split"
          >

            {/* LEFT 35% — Marketing / Understanding */}
            <div className="text-left" data-testid="landing-hero-copy">
              <p
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-l-border-soft bg-l-bg-soft text-l-hint font-mono uppercase tracking-widest text-l-text-muted mb-7"
                data-testid="landing-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-l-accent" />
                Privacy-first email relay
              </p>

              <h1
                className="font-heading text-l-hero text-l-text"
                data-testid="landing-headline"
              >
                Protect your identity<span className="text-l-accent">.</span>
                <br />
                Send emails anonymously<span className="text-l-accent">.</span>
              </h1>

              <p className="mt-7 text-l-body text-l-text-muted max-w-md">
                Protect your privacy and send emails completely anonymously — free or paid.
              </p>

              <ul className="mt-8 flex flex-col gap-3.5 text-l-body text-l-text font-medium">
                <li className="flex items-center gap-3"><Check size={18} className="text-l-accent shrink-0" /> No personal data required</li>
                <li className="flex items-center gap-3"><Check size={18} className="text-l-accent shrink-0" /> No tracking</li>
                <li className="flex items-center gap-3"><Check size={18} className="text-l-accent shrink-0" /> No identity leakage</li>
                <li className="flex items-center gap-3"><Check size={18} className="text-l-accent shrink-0" /> Works instantly</li>
                <li className="flex items-center gap-3"><Check size={18} className="text-l-accent shrink-0" /> Free &amp; anonymous</li>
              </ul>
            </div>

            {/* RIGHT 65% — Stacked vertical auth cards (Create → Sign in) */}
            <div className="space-y-6" data-testid="landing-hero-stack">

              {/* ── CARD 1 — Choose a plan (PROMPT 026) ───────────────
                  Free-alias self-signup was removed: every new user must
                  go through `/pricing` first. The card layout is preserved
                  for visual continuity with the Sign-in card below. */}
              <div
                className="rounded-l-card-lg border border-l-border bg-l-bg shadow-l-card-hi p-l-card-p sm:p-l-card-p-lg"
                data-testid="landing-create-section"
              >
                <h2 className="text-l-panel-title text-l-text">Choose a plan to start</h2>
                <p className="mt-2 text-l-small text-l-text-muted">
                  Pick a plan that fits — your inbox is created instantly after checkout.
                </p>

                <div className="mt-5 flex flex-col gap-2.5" data-testid="landing-pricing-cta-list">
                  <Link
                    to="/pricing"
                    state={{ scrollPlan: "1day" }}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-l-border bg-l-bg-soft hover:bg-l-bg-hover transition-colors group"
                    data-testid="landing-cta-1day"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-l-accent/10 flex items-center justify-center">
                        <Clock size={15} className="text-l-accent" />
                      </span>
                      <span className="flex flex-col text-left">
                        <span className="text-l-small font-medium text-l-text">1-Day Pass</span>
                        <span className="text-l-hint text-l-text-muted">Full access — €1</span>
                      </span>
                    </span>
                    <ArrowRight size={14} className="text-l-text-subtle group-hover:text-l-text" />
                  </Link>

                  <Link
                    to="/pricing"
                    state={{ scrollPlan: "monthly" }}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-l-border bg-l-bg-soft hover:bg-l-bg-hover transition-colors group"
                    data-testid="landing-cta-monthly"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-l-accent/10 flex items-center justify-center">
                        <Mail size={15} className="text-l-accent" />
                      </span>
                      <span className="flex flex-col text-left">
                        <span className="text-l-small font-medium text-l-text">Monthly</span>
                        <span className="text-l-hint text-l-text-muted">Recurring — best for daily use</span>
                      </span>
                    </span>
                    <ArrowRight size={14} className="text-l-text-subtle group-hover:text-l-text" />
                  </Link>

                  <Link
                    to="/pricing"
                    state={{ scrollPlan: "yearly" }}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-l-border bg-l-bg-soft hover:bg-l-bg-hover transition-colors group"
                    data-testid="landing-cta-yearly"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-l-accent/10 flex items-center justify-center">
                        <Shield size={15} className="text-l-accent" />
                      </span>
                      <span className="flex flex-col text-left">
                        <span className="text-l-small font-medium text-l-text">Yearly</span>
                        <span className="text-l-hint text-l-text-muted">Save more — 12 months</span>
                      </span>
                    </span>
                    <ArrowRight size={14} className="text-l-text-subtle group-hover:text-l-text" />
                  </Link>
                </div>

                <Button
                  asChild
                  className="l-btn-primary mt-5 w-full"
                  data-testid="landing-pricing-cta"
                >
                  <Link to="/pricing">
                    See all plans <ArrowRight size={14} className="ml-2" />
                  </Link>
                </Button>

                <p
                  className="mt-5 text-l-small font-medium text-l-text"
                  data-testid="landing-free-note"
                >
                  No accounts created before checkout
                  <br />
                  Inbox unlocks the moment payment clears
                </p>
              </div>

              {/* ── CARD 1.5 + Sign-in moved (PROMPT 030) ──────────────
                  The Trial card moved out of ROW 1 and into ROW 2 below
                  (centered, two-block layout). The Sign-in card moved
                  into a compact card directly under ROW 2 so the hero
                  reads cleanly as Hero | Pricing. */}
            </div>
          </div>

          {/* ─── ROW 2 (PROMPT 030) — Trial entry, two blocks ─── */}
          <div
            className="mt-12 lg:mt-16 max-w-5xl mx-auto"
            data-testid="landing-row2"
          >
            <div className={`grid ${SIMPLIFIED_FLOW ? "grid-cols-1 max-w-md mx-auto" : "grid-cols-1 md:grid-cols-2"} gap-5 md:gap-6`}>
              {/* BLOCK A — Instant trial (system-generated). In SIMPLIFIED_FLOW
                  this is the ONE and only CTA — single button, no advanced
                  options, no custom-address fields. */}
              <div
                className="rounded-l-card-lg border border-dashed border-l-border bg-l-bg-soft p-l-card-p sm:p-l-card-p-lg flex flex-col"
                data-testid="landing-trial-instant-block"
              >
                <h3 className="text-l-panel-title text-l-text">
                  {SIMPLIFIED_FLOW ? "Get an anonymous inbox" : "Try it instantly"}
                </h3>
                <p className="mt-2 text-l-small text-l-text-muted">
                  {SIMPLIFIED_FLOW
                    ? "One tap and your temporary inbox is ready."
                    : "Perfect for verification, signups and one-time use."}
                </p>

                {/* Footer cluster — pinned to the bottom so the CTA aligns
                    with Card B's CTA at the same Y. The hint reserves the
                    same vertical slot in both cards (see Card B below). */}
                <div className="mt-auto pt-6">
                  <Button
                    onClick={() => handleStartTrial("random")}
                    disabled={trialCreating}
                    className="l-btn-primary w-full"
                    data-testid="landing-trial-start-random"
                  >
                    {trialCreating ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <>{SIMPLIFIED_FLOW ? "Start now" : "Go to your anonymous inbox"} <ArrowRight size={14} className="ml-2" /></>
                    )}
                  </Button>
                  <p className="mt-3 text-l-hint text-l-text-subtle">
                    No login · No password · No account
                  </p>
                </div>
              </div>

              {/* BLOCK B — Choose your own address (HIDDEN in SIMPLIFIED_FLOW). */}
              {!SIMPLIFIED_FLOW && (
              <div
                className="rounded-l-card-lg border border-l-border bg-l-bg shadow-l-card-hi p-l-card-p sm:p-l-card-p-lg flex flex-col"
                data-testid="landing-trial-custom-block"
              >
                <h3 className="text-l-panel-title text-l-text">Or create your own address</h3>
                <p className="mt-2 text-l-small text-l-text-muted">
                  Pick a personal handle and try it for the next 30 minutes.
                </p>

                {/* Single, responsive input */}
                <input
                  type="text"
                  placeholder="yourname"
                  value={trialAlias}
                  onChange={(e) => handleTrialChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleStartTrial("custom")}
                  autoComplete="off"
                  inputMode="email"
                  aria-label="Desired alias for trial"
                  className={`l-input w-full mt-4 ${trialError ? "border-red-500" : ""}`}
                  data-testid="landing-trial-alias-input"
                />

                {/* Live preview with domain "dropdown" placeholder */}
                <div
                  className="mt-2 flex items-stretch gap-2 text-[12px]"
                  data-testid="landing-trial-preview-row"
                  aria-live="polite"
                >
                  <div
                    className="flex-1 min-w-0 px-3 py-2 rounded-md border border-l-border-soft bg-l-bg-soft font-mono text-l-text truncate"
                    data-testid="landing-trial-preview-local"
                    title={trialAlias || "yourname"}
                  >
                    {trialAlias || <span className="text-l-text-subtle">yourname</span>}
                  </div>
                  <button
                    type="button"
                    disabled
                    aria-label="Domain — more domains coming soon"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-l-border-soft bg-l-bg-soft font-mono text-l-text-muted cursor-not-allowed disabled:opacity-100"
                    data-testid="landing-trial-domain-dropdown"
                    title="More domains coming soon"
                  >
                    {TRIAL_DOMAIN}
                    <ChevronDown size={12} className="text-l-text-subtle" />
                  </button>
                </div>

                {/* Availability / validation status */}
                {trialAvail && (
                  <p
                    className={`mt-2 text-l-hint flex items-center gap-1.5 ${
                      trialAvail.status === "ok"
                        ? "text-emerald-600"
                        : trialAvail.status === "checking"
                          ? "text-l-text-subtle"
                          : "text-red-600"
                    }`}
                    data-testid="landing-trial-availability"
                    data-status={trialAvail.status}
                  >
                    {trialAvail.status === "ok" && <Check size={12} />}
                    {trialAvail.status === "checking" && <Loader2 size={11} className="animate-spin" />}
                    {trialAvail.text}
                  </p>
                )}

                {trialError && !trialAvail && (
                  <p className="mt-2 text-l-hint text-red-600" data-testid="landing-trial-error">
                    {trialError}
                  </p>
                )}

                {/* Footer cluster — same structure as Card A so both CTAs
                    sit at exactly the same Y. The invisible hint placeholder
                    reserves the vertical slot Card A uses for its hint. */}
                <div className="mt-auto pt-6">
                  <Button
                    onClick={() => handleStartTrial("custom")}
                    disabled={trialCreating || !trialAlias.trim() || trialAvail?.status === "taken" || trialAvail?.status === "invalid"}
                    className="l-btn-primary w-full"
                    data-testid="landing-trial-start-custom"
                  >
                    {trialCreating ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <>Start with this address <ArrowRight size={14} className="ml-2" /></>
                    )}
                  </Button>
                  <p className="mt-3 text-l-hint invisible select-none" aria-hidden="true">.</p>
                </div>
              </div>
              )}
            </div>

            {/* Compact sign-in row */}
            <div
              className="mt-8 max-w-md mx-auto rounded-l-card-lg border border-l-border bg-l-bg shadow-l-card-hi p-l-card-p"
              data-testid="landing-login-box"
            >
              <p className="text-l-small text-l-text-muted mb-3 text-center">
                Already have an account?
              </p>
              <form onSubmit={handleLogin} className="space-y-3">
                <input
                  ref={loginEmailRef}
                  type="text"
                  placeholder="Email or username"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  autoComplete="username"
                  aria-label="Email address or username"
                  className="l-input w-full"
                  data-testid="landing-login-email"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  autoComplete="current-password"
                  className="l-input w-full"
                  data-testid="landing-login-password"
                />
                <div
                  className="pt-1 flex justify-center"
                  data-testid="landing-turnstile-container"
                >
                  <TurnstileWidget onToken={setTurnstileToken} size="flexible" />
                </div>
                <Button
                  type="submit"
                  onClick={handleLogin}
                  disabled={loggingIn || !loginEmail.trim() || !loginPassword.trim()}
                  className="l-btn-primary w-full"
                  data-testid="landing-login-button"
                >
                  {loggingIn ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <>Log in <ArrowRight size={14} className="ml-2" /></>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="border-t border-l-border-soft bg-l-bg">
        <div className="max-w-6xl mx-auto px-5 py-l-section-y">
          <div className="text-center mb-14">
            <h2 className="font-heading text-l-section text-l-text">Why QuickInbox</h2>
            <p className="mt-4 text-l-body text-l-text-muted max-w-xl mx-auto">Everything you need to stay anonymous — no compromises.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-l-gap" data-testid="landing-features">
            {[
              { icon: EyeOff,      title: "Hide your identity",         desc: "Your real email stays private — always." },
              { icon: Zap,         title: "Eliminate spam completely",  desc: "Kill the alias, the spam disappears with it." },
              { icon: Lock,        title: "Control who reaches you",    desc: "Decide what gets through, and what doesn't." },
              { icon: Fingerprint, title: "Create instant identities",  desc: "New alias in seconds — no signup required." },
              { icon: Layers,      title: "Separate your digital life", desc: "One alias per service. Zero cross-exposure." },
              { icon: Eye,         title: "Trust before you commit",    desc: "Test services without risking your real inbox." },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-l-card-p rounded-l-card border border-l-border bg-l-bg flex items-start gap-4 shadow-l-card transition-all hover:border-l-accent hover:shadow-l-card-hi"
                data-testid={`feature-${title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-l-btn bg-l-accent/10 border border-l-accent/20 flex items-center justify-center shrink-0">
                  <Icon size={22} className="text-l-accent" />
                </div>
                <div className="flex flex-col min-w-0">
                  <div className="l-card-feat__title">
                    <h3 className="text-l-card-title text-l-text">{title}</h3>
                  </div>
                  <div className="l-card-feat__body mt-1.5">
                    <p className="text-l-small text-l-text-muted">{desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING — exactly 3 plans: Daily | Monthly (highlighted) | Yearly ═══ */}
      <section id="pricing" className="border-t border-l-border-soft bg-l-bg-soft">
        <div className="max-w-6xl mx-auto px-5 py-l-section-y">
          <div className="text-center mb-14">
            <h2 className="font-heading text-l-section text-l-text">Simple, flexible plans</h2>
            <p className="mt-4 text-l-body text-l-text-muted max-w-xl mx-auto">Pay only for what you use. Cancel anytime.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-l-gap lg:gap-l-gap-lg max-w-5xl mx-auto items-stretch" data-testid="landing-pricing">
            {plansLoading && (
              <div className="rounded-l-card border border-l-border bg-l-bg p-8 flex items-center justify-center col-span-full">
                <Loader2 size={18} className="animate-spin text-l-text-subtle" />
              </div>
            )}
            {!plansLoading && plans.length === 0 && (
              <div className="rounded-l-card border border-l-border bg-l-bg p-8 text-center text-l-small text-l-text-muted col-span-full">
                Pricing temporarily unavailable.
              </div>
            )}

            {!plansLoading && plans.map((plan) => {
              // Map plan.key → fixed German badge with RAL-spec palette.
              // Both badges share the `l-badge-ral` composite token — identical
              // size / rotation / anchor. Only the modifier colour differs.
              const BADGE = {
                pro_monthly: { text: "Am beliebtesten", mod: "l-badge-ral--melon" },
                pro_yearly:  { text: "Spare 17%",       mod: "l-badge-ral--green" },
              }[plan.key];

              const isHi = plan.key === "pro_monthly";

              return (
                <div
                  key={plan.key}
                  className={`relative rounded-l-card p-l-card-p lg:p-l-card-p-lg flex flex-col transition-all shadow-l-card ${
                    isHi
                      ? "border-2 border-l-accent bg-l-bg-soft ring-4 ring-l-accent-ring/50 shadow-l-card-hi"
                      : "border border-l-border bg-l-bg hover:border-l-text-subtle hover:-translate-y-0.5 hover:shadow-l-card-hi"
                  }`}
                  data-testid={`landing-plan-${plan.key}`}
                >
                  {/* Diagonal RAL badge — identical size/rotation/anchor via .l-badge-ral */}
                  {BADGE && (
                    <span
                      className={`l-badge-ral ${BADGE.mod}`}
                      data-testid={`landing-plan-badge-${plan.key}`}
                    >
                      {BADGE.text}
                    </span>
                  )}

                  <div className="mb-4">
                    <h3 className="font-heading text-l-card-title text-l-text">{plan.name}</h3>
                    <p className="text-l-small text-l-text-muted mt-1.5">{plan.description}</p>
                  </div>

                  <div className="flex items-baseline gap-1.5 mb-6">
                    <span className="font-heading text-l-price text-l-text">
                      €{Number.isInteger(plan.price) ? plan.price.toFixed(0) : plan.price.toFixed(2)}
                    </span>
                    <span className="text-l-small text-l-text-muted">/ {plan.duration_unit}</span>
                    {plan.compare_at_price && (
                      <span className="ml-2 text-l-hint text-l-text-subtle line-through">€{plan.compare_at_price.toFixed(0)}</span>
                    )}
                  </div>

                  <Button
                    onClick={() => handleUpgrade(plan)}
                    disabled={checkoutLoadingKey === plan.key}
                    className="l-btn-primary mt-auto"
                    data-testid={`landing-plan-cta-${plan.key}`}
                  >
                    {checkoutLoadingKey === plan.key
                      ? <Loader2 size={15} className="animate-spin" />
                      : <>Choose {plan.name} <ArrowRight size={14} className="ml-2" /></>}
                  </Button>
                </div>
              );
            })}
          </div>

          <p className="text-l-small text-l-text-muted text-center mt-10">
            Secure checkout via Stripe. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ═══ HOW IT WORKS — strong 1-2-3 with arrow connectors ═══ */}
      <section id="how-it-works" className="border-t border-l-border-soft bg-l-bg">
        <div className="max-w-6xl mx-auto px-5 py-l-section-y">
          <div className="text-center mb-16">
            <h2 className="font-heading text-l-section text-l-text">Go anonymous in seconds</h2>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-4 max-w-5xl mx-auto">
            {[
              { num: "01", icon: Key,       title: "Generate an alias",        desc: "Create a random email identity instantly." },
              { num: "02", icon: Inbox,     title: "Receive emails privately", desc: "Messages arrive without exposing you." },
              { num: "03", icon: RotateCcw, title: "Stay in control",          desc: "Disable, rotate, or delete anytime." },
            ].map(({ num, icon: Icon, title, desc }, idx, arr) => (
              <Fragment key={num}>
                <div className="flex-1 text-center space-y-4 max-w-[280px]">
                  <div className="w-16 h-16 mx-auto rounded-l-card bg-l-bg border border-l-border shadow-l-card flex items-center justify-center">
                    <Icon size={28} className="text-l-accent" strokeWidth={1.8} />
                  </div>
                  <p className="font-heading text-l-step-num text-l-accent" data-testid={`step-num-${num}`}>{num}</p>
                  <h3 className="text-l-step-title text-l-text">{title}</h3>
                  <p className="text-l-body text-l-text-muted">{desc}</p>
                </div>
                {idx < arr.length - 1 && (
                  <div className="flex items-center justify-center text-l-accent/60 shrink-0" aria-hidden="true">
                    <ArrowRight size={32} className="hidden sm:block" strokeWidth={1.5} />
                    <ChevronDown size={32} className="sm:hidden" strokeWidth={1.5} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="border-t border-l-border-soft bg-l-bg-soft">
        <div className="max-w-6xl mx-auto px-5 py-l-section-y text-center">
          <h2 className="font-heading text-l-final text-l-text max-w-2xl mx-auto" data-testid="final-cta-headline">
            How far would you go to protect your privacy?
          </h2>
          <p className="mt-6 text-l-body text-l-text-muted max-w-xl mx-auto">
            You&rsquo;re almost there.
            <br />
            Start your anonymous inbox now.
          </p>
          <div className="mt-10">
            <Button
              onClick={() => scrollTo("alias-input")}
              className="l-btn-primary l-btn-primary-lg inline-flex w-auto"
              data-testid="final-cta"
            >
              Start Anonymous Inbox <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-l-border-soft py-8 bg-l-bg">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-l-footer text-l-text-muted font-medium" data-testid="footer-copyright">QuickInbox © 2026</p>
          <div className="flex items-center gap-8 text-l-footer text-l-text-muted">
            <Link to="/claim" className="hover:text-l-text transition-colors" data-testid="footer-access">Access inbox</Link>
            <button
              type="button"
              onClick={() => scrollTo("pricing")}
              className="hover:text-l-text transition-colors"
              data-testid="footer-pricing"
            >
              Pricing
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
